import { inject, useContext } from '@nuxtjs/composition-api';
import { isMatch, uniqueId, cloneDeep } from 'lodash';
import { ModalContextSymbol } from '~/modules/common/components/ui/OModal/ModalProvider';
import ODialog from '~/modules/common/components/ui/OModal/ODialog.vue';
import OToast from '~/modules/common/components/ui/OModal/OToast.vue';
import ONotify from '~/modules/common/components/ui/OModal/ONotify.vue';

/**
 * Use Modal
 * Imperative api for modals, dialogs and toasts
 *
 * @returns [ openModal, closeModal ]
 */
export const useModal = () => {
  const context = inject(ModalContextSymbol);

  /**
   * Open a modal
   *
   * @param {Object} modal Modal to open (Vue Component)
   * @param {Object} [props] props to pass to the modal - optional
   * @param {String} [label] aria label - optional
   * @param {String} [role] aria role - optional
   * @param {Boolean} [userDismiss=true] enable or disable click outside - optional
   */
  const openModal = async (modalImport, props = {}, label = '', role = 'dialog', userDismiss = true) => {
    let modal;
    if (modalImport instanceof Promise) {
      const { default: component } = await modalImport;
      modal = component;
    } else {
      modal = modalImport;
    }
    // Modal await yapacak promise
    let resolver;
    const modalPromise = new Promise((resolve) => {
      resolver = resolve;
    });

    // Modal'larin default close methodu (props verirken close ile ezileblir)
    const defaultCloseFn = (data) => {
      // eger modal icindeki close prop'u direk kullanildiysa
      // mouse eventi ignore et:
      // <button @click="close">
      // eslint-disable-next-line no-prototype-builtins
      if (data?.hasOwnProperty('isTrusted')) data = null;
      closeModal(modal, data);
    };
    // modal'a gonderilen props
    const propsData = { close: defaultCloseFn, ...props };

    const modalEntry = {
      modal,
      props: propsData,
      resolver,
      label,
      role,
      userDismiss,
    };

    context.addModal(modalEntry);

    return modalPromise;
  };

  /**
   * Close a Modal
   * @param {Object} modal Modal to close (Vue Component)
   * @param {Object} [result] data to return when modal closes
   */
  const closeModal = (modal, result) => {
    const modalFound = context.modalEntries.find((modalEntry) => isMatch(modalEntry.modal, modal));
    if (modalFound) {
      modalFound?.resolver(result); // promise cağır
      context.removeModal(modalFound);
    }
  };

  /**
   * Show Login Modal
   *
   * @info Login Modal hemen hemen her sayfada gerekli olduğu için böyle bir helper method yapıldı. Her modal için bunu kullanmayın!
   * @info Eğer aşağıdaki gibi bir method oluşturursanız modal sayfada kullanılsada kullanılmasada bundle edilir!
   */
  const openLoginModal = () => {
    return openModal(import('~/modules/auth/components/ModalLogin' /* webpackChunkName: "ModalLogin" */));
  };

  return { openModal, closeModal, openLoginModal };
};

/**
 * Use Dialog
 * Imperative api for dialogs
 *
 * @returns [ openModal, closeModal ]
 */
export const useDialog = () => {
  const { openModal } = useModal();
  const { i18n } = useContext();

  /**
   * Open a dialog
   * @param {Object} params
   * @param {Object} [params.title]
   * @param {Object} params.description
   * @param {Array} params.actions butonlar
   */
  const openDialog = (params) => {
    const newDialog = cloneDeep(ODialog);
    // bütün dialog objeleri aynı componenti kullandığı için dialog kapatılırken isMatch(eşit mi) kontrolü yaparken componentın name alanı farklı olacağı için daha kolay bulunacak.
    newDialog.name = uniqueId('dialog_');
    return openModal(newDialog, params);
  };

  /**
   * Show Error Message
   *
   * @info backendden gelen hata mesaji ya da default mesaji ekranda gösterir ya da custom mesaj girebilirsiniz
   *
   * @example
   * openErrorDialog() // "Bir hata meydana geldi" şeklinde olan default açıklamayı ekranda gösterir.
   * openErrorDialog({error}) // backenddden gelen hatayi ekranda gösterir yoksa default açıklama gösterilir
   * openErrorDialog({description: 'hello'}) // backend'den bağımsız sizin yazdığınız hata mesajını gösterir
   * openErrorDialog({error, description: 'error varsa bas yoksa bu mesaji goster' }) // backendde hata varsa gösterir yoksa sizin yazdığınız açıklamayı gösterir
   *
   * @param {Object} [error] error object - optional
   * @param {string} [description] custom message optional - optional
   */
  const openErrorDialog = ({ error = null, description = i18n.t('main.general-error') } = {}) => {
    const errorText = (error && error?.response?.data?.message) || description;
    openDialog({ description: errorText });
  };

  return { openDialog, openErrorDialog };
};

/**
 * Use Toast
 * Imperative api for toast
 *
 * @returns [ openModal, closeModal ]
 */
export const useToast = (params) => {
  const { openModal, closeModal } = useModal();

  /**
   * Open a toast
   * @param {string} message
   * @param {number} timeout
   */
  const toastOk = (message, timeout = 5000) => {
    setTimeout(function () {
      closeModal(OToast);
    }, timeout);
    return openModal(OToast, { message, variant: 'default', ignoreModal: true });
  };

  const toastSuccess = (message, timeout = 5000) => {
    setTimeout(function () {
      closeModal(OToast);
    }, timeout);
    return openModal(OToast, { message, variant: 'success', ignoreModal: true });
  };

  const toastDanger = (message, timeout = 5000) => {
    setTimeout(function () {
      closeModal(OToast);
    }, timeout);
    return openModal(OToast, { message, variant: 'danger', ignoreModal: true });
  };

  return { toastOk, toastSuccess, toastDanger };
};

/**
 * Use Notify
 * Imperative api for notify
 *
 * @returns [ openModal, closeModal ]
 */
export const useNotify = (params) => {
  const { openModal, closeModal } = useModal();

  /**
   * Open a notify
   * @param {string} message
   * @param {number} timeout
   * @param {String} actionName
   * @param {String} actionUrl
   * @param {String} position
   */
  const notifySuccess = (message, { actionName, actionUrl, timeout = 3000, position }) => {
    setTimeout(function () {
      closeModal(ONotify);
    }, timeout);
    return openModal(ONotify, { message, actionName, actionUrl, position });
  };

  return { notifySuccess };
};
